import { NavLink, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Activity, CalendarClock, ChartNoAxesCombined, FilePenLine, Globe2, Home, KeyRound, Link2, LogOut, Menu, Radio, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { assets } from "@/lib/assets";
import "@/components/SidebarEnhancement.css";
import { AIDoctorAssistant } from "@/components/AIDoctorAssistant";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ContentGenerationStatus } from "@/components/ContentGenerationStatus";

const nav = [
  ["/app", "Overview", Home, assets.health], ["/app/keywords", "Keyword Chase", KeyRound, assets.keyword],
  ["/app/content", "Content Creation", FilePenLine, assets.content], ["/app/social", "Social Signals", Radio, assets.social],
  ["/app/radar", "AI Visibility", Sparkles, assets.radar], ["/app/backlinks", "Backlinks", Link2, assets.backlinks],
  ["/app/analytics", "Live Visitors", Activity, assets.liveVisitors],
];

export function NavItems({ close }) {
  return <nav className="nav-list" data-testid="primary-navigation">{nav.map(([to, label, Icon, artwork]) => (
    <NavLink key={to} to={to} end={to === "/app"} onClick={close} data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-")}`} className={({ isActive }) => `nav-item ${artwork ? "nav-item-art" : ""} ${isActive ? "active" : ""}`}>
      {artwork ? <img className="nav-artwork" src={artwork} alt={`${label} module`} data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-")}-artwork`} /> : <span className="nav-icon"><Icon size={22} strokeWidth={2.1} /></span>}
      <span className="nav-label">{label}</span>{label === "Live Visitors" && <i className="live-dot" />}
    </NavLink>
  ))}</nav>;
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trialDays = Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000));
  const handleLogout = () => {
    const logoutRequest = logout();
    queryClient.clear();
    ["srd_site_id", "srd_site_domain", "srd_doctor_session", "srd_doctor_dismissed"].forEach((key) => localStorage.removeItem(key));
    navigate("/login", { replace: true });
    logoutRequest.catch(() => toast.error("Your session could not be closed. Please try again."));
  };
  return <div className="app-shell">
    <aside className="sidebar" data-testid="desktop-sidebar">
      <NavLink to="/app" className="brand" data-testid="sidebar-logo-link"><img src={assets.transparentLogo} alt="SEO Rank Doctor" /></NavLink>
      <NavItems />
      <div className="sidebar-pulse" data-testid="sidebar-tracker-status"><ChartNoAxesCombined size={18} /><div><strong>Tracker healthy</strong><span>Events arriving now</span></div></div>
    </aside>
    <main className="main-area">
      <header className="mobile-header">
        <img src={assets.logo} alt="SEO Rank Doctor" />
        <Sheet><SheetTrigger asChild><Button variant="outline" size="icon" data-testid="mobile-menu-button" aria-label="Open navigation"><Menu size={20} /></Button></SheetTrigger><SheetContent side="left" className="mobile-sheet"><SheetTitle className="sr-only">Product navigation</SheetTitle><SheetDescription className="sr-only">Open a seorank.doctor dashboard module.</SheetDescription><NavItems /></SheetContent></Sheet>
      </header>
      <div className="top-strip" data-testid="workspace-status"><span><Globe2 size={15} /> seorank.doctor</span><ContentGenerationStatus/><span data-testid="trial-days-remaining"><CalendarClock size={15} /> {trialDays} trial days remaining</span><span className="workspace-user" data-testid="workspace-user"><b>{user.name}</b><button type="button" onClick={handleLogout} data-testid="logout-button" aria-label="Log out" title="Log out"><LogOut size={15}/></button></span></div>
      <div className="page-wrap">{children}</div>
      <AIDoctorAssistant />
    </main>
  </div>;
}
